import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import Seo from "../components/Seo";

// Admin MFA self-recovery: for when the authenticator AND recovery codes are lost.
// Requires the account password, then emails a one-time link to the account's own
// registered address. Unusual requests complete only after a 24h waiting period.
export default function MfaRecovery() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const wantCancel = searchParams.get("cancel") === "1";
  const [form, setForm] = useState({ email: "", password: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState("");

  useEffect(() => {
    if (!token) return;
    api.get("/auth/mfa/recovery/status", { params: { token } })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ valid: false }));
  }, [token]);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/mfa/recovery/request", form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/mfa/recovery/confirm", { token });
      setResult(r.data?.scheduled ? "scheduled" : "done");
    } catch (err) {
      setError(formatApiError(err, "That link is invalid or has expired. Request a fresh one below."));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/mfa/recovery/cancel", { token });
      setResult("cancelled");
    } catch (err) {
      setError(formatApiError(err, "That recovery request is no longer active."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10" data-testid="mfa-recovery-page">
      <Seo title="Recover admin access | HiredToday" description="Recover access to your HiredToday admin account." path="/mfa-recovery" noindex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 rounded-md bg-zinc-950 text-white flex items-center justify-center mb-4">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Recover admin access</h1>
          <p className="mt-2 text-sm text-zinc-500">For admins who have lost their authenticator and recovery codes.</p>
        </div>

        {token ? (
          result === "done" ? (
            <div className="border border-green-200 bg-green-50 rounded-md p-6 text-center" data-testid="mfa-recovery-done">
              <p className="font-semibold text-green-800">MFA has been turned off</p>
              <p className="mt-2 text-sm text-green-700">Your existing sessions were signed out. Log in with your password and set MFA up again in Control Tower → Security.</p>
              <Link to="/login" className="mt-4 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md" data-testid="mfa-recovery-login">Go to sign in</Link>
            </div>
          ) : result === "scheduled" ? (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-6" data-testid="mfa-recovery-scheduled">
              <p className="font-semibold text-amber-900">24-hour security waiting period</p>
              <p className="mt-2 text-sm text-amber-800">Because this request looks unusual, turning MFA off completes after 24 hours. You'll get a final confirmation email when it's done. Check your inbox if you want to cancel.</p>
            </div>
          ) : result === "cancelled" ? (
            <div className="border border-zinc-200 rounded-md p-6 text-center" data-testid="mfa-recovery-cancelled">
              <p className="font-semibold text-zinc-900">Recovery request cancelled</p>
              <p className="mt-2 text-sm text-zinc-500">Nothing has changed — MFA is still on for your account.</p>
            </div>
          ) : !status ? (
            <div className="h-32 rounded-md bg-zinc-50 animate-pulse" data-testid="mfa-recovery-loading" />
          ) : !status.valid ? (
            <div className="border border-zinc-200 rounded-md p-6 text-center" data-testid="mfa-recovery-invalid">
              <p className="font-semibold text-zinc-900">That recovery link is invalid or has expired</p>
              <p className="mt-2 text-sm text-zinc-500">Links work once and expire after 30 minutes.</p>
              <Link to="/mfa-recovery" data-testid="mfa-recovery-restart"
                className="mt-4 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Request a fresh link
              </Link>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-md p-6" data-testid="mfa-recovery-confirm-card">
              <p className="font-semibold text-zinc-900">{wantCancel ? "Cancel this recovery request?" : "Turn off MFA on your admin account?"}</p>
              <p className="mt-2 text-sm text-zinc-500">
                {wantCancel
                  ? "Nothing changes — MFA stays on."
                  : status.unusual
                    ? "For security this completes after a 24-hour waiting period. All your sessions will then be signed out and we'll email you a confirmation."
                    : "MFA will be turned off immediately, all your sessions will be signed out, and we'll email you a confirmation. You can set MFA up again any time."}
              </p>
              <button onClick={wantCancel ? cancel : confirm} disabled={busy} data-testid="mfa-recovery-confirm"
                className={`mt-4 w-full h-11 text-sm font-semibold rounded-md transition-colors duration-200 disabled:opacity-50 ${wantCancel ? "border border-zinc-300 text-zinc-700 hover:bg-zinc-50" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}>
                {busy ? "One moment…" : wantCancel ? "Yes, cancel the request" : status.unusual ? "Start the 24-hour waiting period" : "Yes, turn off MFA"}
              </button>
            </div>
          )
        ) : sent ? (
          <div className="border border-zinc-200 rounded-md p-6 text-center" data-testid="mfa-recovery-sent">
            <p className="font-semibold text-zinc-900">Check your inbox</p>
            <p className="mt-2 text-sm text-zinc-500">If that admin account exists and has MFA turned on, we've sent a recovery link to its registered email address. The link expires in 30 minutes.</p>
          </div>
        ) : (
          <form onSubmit={request} className="border border-zinc-200 rounded-md p-6 space-y-4" data-testid="mfa-recovery-form">
            <p className="text-sm text-zinc-500">Enter your admin email and password. We'll email a recovery link to the account's registered address — nothing changes until you confirm from that email.</p>
            <div>
              <label htmlFor="mr-email" className="block text-sm font-medium text-zinc-900 mb-1.5">Email</label>
              <input id="mr-email" type="email" required value={form.email} data-testid="mfa-recovery-email"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label htmlFor="mr-password" className="block text-sm font-medium text-zinc-900 mb-1.5">Password</label>
              <input id="mr-password" type="password" required value={form.password} data-testid="mfa-recovery-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <button type="submit" disabled={busy} data-testid="mfa-recovery-submit"
              className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {busy ? "Sending…" : "Email me a recovery link"}
            </button>
          </form>
        )}
        {error && <p className="mt-4 text-sm text-red-600 text-center" data-testid="mfa-recovery-error">{error}</p>}
        <p className="mt-6 text-center text-sm text-zinc-500">
          Remembered your codes? <Link to="/login" className="text-zinc-950 underline underline-offset-2">Back to sign in</Link>
        </p>
      </div>
    </main>
  );
}
